import React, { useMemo, useContext } from "react";
import ChartContainer from "features/metadesk/CommunityAnalysis/components/chart/ChartContainer";
import { get, isEmpty, map } from "lodash-es";
import { analysisColors } from "features/configure/chartColors";
import { Box } from "@mui/material";
import { camelCaseToTitle } from "features/helpers/utils";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import * as echarts from "echarts"; // 引入 echarts
import EcoScanContext from "features/metadesk/EcoScan/contexts/EcoScan.context";
import { useIntl } from "react-intl";

const lineChartBackgroundColor = [
  {
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 1,
        color: "rgba(255,255,255,0.1)",
      },
      {
        offset: 0,
        color: "rgba(249,232,81,0.3)",
      },
    ]),
  },
  {
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 1,
        color: "rgba(255,255,255,0.1)",
      },
      {
        offset: 0,
        color: "rgba(254,202,85,0.3)",
      },
    ]),
  },
  {
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 1,
        color: "rgba(255,255,255,0.1)",
      },
      {
        offset: 0,
        color: "rgba(246,160,109,0.3)",
      },
    ]),
  },
  {
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 1,
        color: "rgba(255,255,255,0.1)",
      },
      {
        offset: 0,
        color: "rgba(226,125,134,0.3)",
      },
    ]),
  },
  {
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 1,
        color: "rgba(255,255,255,0.1)",
      },
      {
        offset: 0,
        color: "rgba(172,85,122,0.3)",
      },
    ]),
  },
];

const LineChartContainer = ({
  title,
  data,
  isLoading,
  projectPartyDataLength,
  type,
}) => {
  const { formatMessage } = useIntl();
  const colors = analysisColors.middle;

  const dataTitleMap = {
    "Wallet Count": formatMessage({
      id: "ecoScan.header.walletCount",
    }),
    transactionCount: formatMessage({
      id: "global.metrics.transaction",
    }),
    "Retention Rate": formatMessage({
      id: "ecoScan.header.retentionRate",
    }),
    newDiscordMember: formatMessage({
      id: "ecoScan.header.newDiscordMember",
    }),
    newTelegramMember: formatMessage({
      id: "ecoScan.header.newTelegramMember",
    }),
    engagementRate: formatMessage({
      id: "ecoScan.header.engagementRate",
    }),
  };

  const { dateRange, selectedParty, setSelectedParty, overviewFilterValue } =
    useContext(EcoScanContext);

  const projectPartyNames = isEmpty(data)
    ? []
    : Object.keys(data[0]).filter((key) => key !== "date");

  const projectPartySeriesData = useMemo(() => {
    return isEmpty(projectPartyNames)
      ? []
      : projectPartyNames.map((project, index) => {
          return {
            name: project,
            type: "line",
            lineStyle: {
              color: colors[index % colors.length],
              width: 2,
            },
            itemStyle: {
              color: colors[index % colors.length],
              borderColor: colors[0],
              borderWidth: 2,
            },
            areaStyle: {
              color: "transparent",
            },
            emphasis: {
              focus: "series",
              areaStyle: {
                color: colors[index % colors.length],
                opacity: 0.1,
              },
            },
            data: data.map((item) => item[project]),
          };
        });
  }, [projectPartyNames]);

  const projectPartyDataForChart = useMemo(() => {
    return {
      showDate: data.map((item) => item.date),
      seriesData: projectPartySeriesData,
    };
  }, [projectPartySeriesData, data]);

  const mdNumber = useMemo(() => {
    switch (projectPartyDataLength) {
      case 1:
        return 12;
      case 4:
        return 6;
      case 3:
        return 4;
      default:
        return 12;
    }
  }, [projectPartyDataLength]);

  const renderChartTitleInChartTooltip = () => {
    if (title === "overview") {
      return camelCaseToTitle(overviewFilterValue);
    } else {
      return camelCaseToTitle(title);
    }
  };

  return (
    <Grid item xs={12} md={mdNumber}>
      <Box
        sx={{
          fontWeight: 500,
          fontSize: "16px",
          marginBottom: "8px",
        }}
        color={(theme) => theme.customColors.grey[700]}
      >
        {dataTitleMap[title] || camelCaseToTitle(title)}
      </Box>
      <ChartContainer
        chartStyle="line"
        chartData={projectPartyDataForChart}
        isLoading={isLoading}
        options={{
          chartTitle: renderChartTitleInChartTooltip(),
          grid: {
            bottom: "0%", // 控制圖表下方距離
            left: "2%", // 控制圖表左側距離
            right: "3%", // 控制圖表右側距離
            containLabel: true, // 確保標籤不會被裁切
          },
        }}
      />
    </Grid>
  );
};

export default LineChartContainer;
